import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    ArrowLeft, Phone, Mail, User, Ruler, Scale,
    Stethoscope, Activity, Heart, Home, MapPin, Building,
    Clock, CheckCircle, Edit, Truck,
    Package, User2, X, Loader
} from 'lucide-react';
import { ordersApi } from '../services/api';
import { formatDate } from '../utils/helpers';
import { useTheme } from '../context/ThemeContext';
import { Modal, message } from 'antd';
import { usePermissions } from '../hooks/Usepermissions';
import { BASE_URL as API_URL, ASSET_BASE_URL as ASSET_URL } from '../services/api';

const COURIER_APIS = {
    delhivery:  { name: 'Delhivery',  logo: '📮' },
    shiprocket: { name: 'Shiprocket', logo: '🚀' },
    fship:      { name: 'FShip',      logo: '🚢' },
    shadowfax:  { name: 'Shadowfax',  logo: '⚡' },
    ekart:      { name: 'Ekart',      logo: '🟠' },
    parcelx:    { name: 'ParcelX',    logo: '📦' },
};

const InfoCard = ({ icon: Icon, label, value, subLabel, bgColor, iconColor, themeColor, isDark }) => (
    <div className={`p-4 rounded-2xl flex items-start gap-4 min-w-[140px] flex-1 border transition-all ${isDark ? 'bg-white/5 border-white/5 hover:bg-white/10 hover:border-white/10' : 'bg-white border-gray-50 shadow-sm'}`} 
         style={!isDark && bgColor ? { backgroundColor: bgColor } : {}}>
        {Icon && <Icon className="w-5 h-5 mt-0.5" style={{ color: iconColor || themeColor }} />}
        <div>
            <p className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{label}</p>
            <p className={`text-sm font-black mt-1 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{value}</p>
            {subLabel && <p className={`text-[10px] font-black mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{subLabel}</p>}
        </div>
    </div>
);

const EDIT_ALLOWED_STATUSES = ['PROCESSING', 'APPROVAL_PENDING', 'CONFIRMED', 'ON_HOLD', 'REJECTED', 'PENDING'];

const SectionHeader = ({ title, showEdit = true, orderId, onEdit, orderStatus, canEdit: canEditProp, isDark, themeColor }) => {
    const canEdit = canEditProp !== undefined
        ? canEditProp && showEdit && EDIT_ALLOWED_STATUSES.includes(orderStatus)
        : showEdit && EDIT_ALLOWED_STATUSES.includes(orderStatus);
    return (
        <div className="flex justify-between items-center mb-6">
            <h3 className={`text-sm font-black uppercase tracking-[0.2em] ${isDark ? 'text-white' : 'text-gray-800'}`}>{title}</h3>
            {canEdit && (
                <button onClick={() => onEdit(`/orders/${orderId}/edit`)} 
                    className={`p-2 rounded-xl transition-all ${isDark ? 'bg-white/5 hover:bg-white/10 text-orange-400' : 'bg-orange-50 hover:bg-orange-100 text-orange-500'}`}>
                    <Edit className="w-4 h-4" />
                </button>
            )}
        </div>
    );
};

const EditAmountModal = ({ isOpen, onClose, order, amountIncrease, setAmountIncrease, isUpdatingAmount, onUpdateAmount, isDark, themeColor }) => {
    const inputRef = React.useRef(null);
    React.useEffect(() => { if (isOpen && inputRef.current) inputRef.current.focus(); }, [isOpen]);
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className={`rounded-3xl w-full max-w-md overflow-hidden relative shadow-2xl border transition-all ${isDark ? 'bg-[#111827] border-white/10' : 'bg-white border-gray-100'}`}>
                <div className="p-6 flex justify-between items-center border-b" style={{ background: isDark ? 'rgba(255,255,255,0.03)' : themeColor + '15', borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'transparent' }}>
                    <h2 className={`text-lg font-black uppercase tracking-widest ${isDark ? 'text-white' : 'text-gray-800'}`}>Edit Summary</h2>
                    <button onClick={onClose} className={`p-1.5 rounded-full transition-all ${isDark ? 'hover:bg-white/10 text-white' : 'hover:bg-black/5 text-gray-500'}`}><X className="w-6 h-6" /></button>
                </div>
                <div className="p-8 space-y-6">
                    <div>
                        <h3 className={`text-[10px] font-black uppercase tracking-widest mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Current Summary</h3>
                        <div className={`rounded-2xl overflow-hidden border ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
                            <div className="p-4 space-y-2 text-[11px] font-black uppercase tracking-widest">
                                <div className="flex justify-between"><span className={isDark ? 'text-gray-500' : 'text-gray-400'}>Sub total</span><span className={isDark ? 'text-white' : 'text-gray-800'}>₹{order.subTotal || order.grandTotal}</span></div>
                                <div className="flex justify-between" style={{ color: themeColor }}><span>Prepaid</span><span>₹{order.prepaidAmount || 0}</span></div>
                                <div className="flex justify-between" style={{ color: themeColor }}><span>Discounts</span><span>₹{order.discount || order.totalDiscounts || 0}</span></div>
                                <div className="flex justify-between text-emerald-500"><span>Shipping</span><span>{order.shippingCharges > 0 ? `₹${order.shippingCharges}` : 'Free'}</span></div>
                            </div>
                            <div className="px-4 py-3 flex justify-between items-center text-white font-black" style={{ backgroundColor: themeColor }}>
                                <span className="text-xs uppercase tracking-widest opacity-80">Amount:</span>
                                <span className="text-xl">₹{Math.max(0, (order.grandTotal || 0) - (order.prepaidAmount || 0))}</span>
                            </div>
                        </div>
                    </div>
                    <div>
                        <label className={`block text-[10px] font-black uppercase tracking-widest mb-2 ${isDark ? 'text-gray-500' : 'text-gray-700'}`}>Amount increase</label>
                        <div className="relative">
                            <input ref={inputRef} type="number" value={amountIncrease} onChange={(e) => setAmountIncrease(e.target.value)} placeholder="0.00"
                                className={`w-full rounded-xl py-3 pl-10 pr-4 text-sm font-black transition-all border outline-none ${isDark ? 'bg-white/5 border-white/10 text-white focus:border-blue-500/50' : 'bg-gray-50 border-gray-200 text-gray-800 focus:border-teal-500'}`} />
                            <span className="absolute left-4 top-3 font-black" style={{ color: themeColor }}>₹</span>
                        </div>
                    </div>
                    <button onClick={onUpdateAmount} disabled={isUpdatingAmount}
                        className={`w-full py-4 text-white font-black text-[10px] uppercase tracking-[0.2em] rounded-xl transition-all shadow-xl active:scale-95 disabled:opacity-30`}
                        style={{ backgroundColor: themeColor, boxShadow: `0 10px 25px -5px ${themeColor}66` }}>
                        {isUpdatingAmount ? <div className="flex items-center justify-center gap-2"><Loader className="w-4 h-4 animate-spin" /><span>Updating...</span></div> : <span>Update Summary</span>}
                    </button>
                </div>
            </div>
        </div>
    );
};

const ReasonModal = ({ reasonModal, setReasonModal, onSubmit, isDark, themeColor }) => {
    const textareaRef = React.useRef(null);
    React.useEffect(() => {
        if (reasonModal.isOpen && textareaRef.current) {
            const len = textareaRef.current.value.length;
            textareaRef.current.setSelectionRange(len, len);
            textareaRef.current.focus();
        }
    }, [reasonModal.isOpen]);
    if (!reasonModal.isOpen) return null;
    const getHeaderColor = () => {
        switch (reasonModal.actionType) {
            case 'REJECT': return isDark ? 'text-red-400 bg-red-400/10' : 'text-red-600 border-red-100 bg-red-50';
            case 'HOLD': return isDark ? 'text-gray-300 bg-gray-300/10' : 'text-gray-700 border-gray-100 bg-gray-50';
            case 'PENDING': return isDark ? 'text-orange-400 bg-orange-400/10' : 'text-orange-600 border-orange-100 bg-orange-50';
            default: return isDark ? 'text-white bg-white/5' : 'text-gray-800 border-gray-100 bg-gray-50';
        }
    };
    const getButtonColor = () => {
        switch (reasonModal.actionType) {
            case 'REJECT': return 'bg-red-500 hover:bg-red-600';
            case 'HOLD': return 'bg-gray-500 hover:bg-gray-600';
            case 'PENDING': return 'bg-orange-500 hover:bg-orange-600';
            default: return themeColor;
        }
    };
    return (
        <div className="fixed inset-0 bg-black/60 flex items-start pt-20 justify-center z-[9999] backdrop-blur-sm">
            <div className={`rounded-2xl w-full max-w-2xl mx-4 shadow-2xl overflow-hidden border ${isDark ? 'bg-[#111827] border-white/10' : 'bg-white border-gray-100'}`}>
                <div className={`px-6 py-4 border-b flex items-center justify-between ${getHeaderColor()}`}>
                    <h3 className="text-lg font-black uppercase tracking-widest">{reasonModal.title}</h3>
                    <button onClick={() => !reasonModal.isLoading && setReasonModal({ ...reasonModal, isOpen: false })} className={`p-1 rounded-full transition-all ${isDark ? 'hover:bg-white/10 text-white' : 'hover:bg-black/5 text-gray-400'}`}>
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-6">
                    <label className={`block text-[10px] font-black uppercase tracking-widest mb-2 ${isDark ? 'text-gray-500' : 'text-gray-700'}`}>Reason <span className="text-red-500">*</span></label>
                    <textarea ref={textareaRef} value={reasonModal.reason} onChange={(e) => setReasonModal({ ...reasonModal, reason: e.target.value })}
                        placeholder="Please provide a valid reason here..." rows={4}
                        className={`w-full px-4 py-3 border rounded-xl outline-none resize-none text-sm font-black transition-all ${isDark ? 'bg-white/5 border-white/10 text-white focus:border-blue-500/50' : 'bg-gray-50 border-gray-200 text-gray-700 focus:border-teal-500'}`} />
                </div>
                <div className={`px-6 py-4 border-t flex items-center justify-end gap-3 ${isDark ? 'bg-white/5 border-white/5' : 'bg-gray-50 border-gray-100'}`}>
                    <button onClick={() => setReasonModal({ ...reasonModal, isOpen: false })}
                        className={`px-6 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all ${isDark ? 'bg-white/5 text-gray-400 hover:text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} disabled={reasonModal.isLoading}>Cancel</button>
                    <button onClick={onSubmit} disabled={reasonModal.isLoading || !reasonModal.reason.trim()}
                        className={`px-8 py-2.5 text-white font-black text-[10px] uppercase tracking-widest rounded-xl flex items-center gap-2 shadow-lg disabled:opacity-30 transition-all active:scale-95`}
                        style={{ backgroundColor: getButtonColor() === themeColor ? themeColor : getButtonColor() }}>
                        {reasonModal.isLoading ? <><Loader className="w-4 h-4 animate-spin" /> Processing...</> : 'Submit'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const AmountDecreaseOrderDetails = () => {
    const { currentTheme } = useTheme();
    const isDark = currentTheme.textColor === '#FFFFFF';
    const { has, isAdmin } = usePermissions();
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const canVerify     = isAdmin || has('VERIFIED_VERIFY');
    const canDispatch   = isAdmin || has('VERIFIED_DISPATCH');
    const canHold       = isAdmin || has('VERIFIED_HOLD');
    const canReject     = isAdmin || has('VERIFIED_REJECT');
    const canPending    = isAdmin || has('VERIFIED_PENDING');
    const canEditAmount = isAdmin || has('VERIFIED_EDIT_AMOUNT');
    const canEdit       = isAdmin || has('VERIFIED_EDIT');
    const canOFDUpdate  = isAdmin || has('OFD_UPDATE');
    const canNDREdit    = isAdmin || has('NDR_EDIT');

    const [activeTab, setActiveTab] = React.useState('callLogs');
    const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
    const [showSuccessPopup, setShowSuccessPopup] = React.useState(false);
    const modalDefaultAmount = '1100';
    const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
    const [loadingOrderDetails, setLoadingOrderDetails] = useState(true);
    const [showDispatchModal, setShowDispatchModal] = useState(false);
    const [selectedCourier, setSelectedCourier] = useState('');
    const [dispatchFormData, setDispatchFormData] = useState({ awbNumber: '', expectedDeliveryDate: '', remarks: '' });
    const [isCreatingShipment, setIsCreatingShipment] = useState(false);
    const [showActionModal, setShowActionModal] = useState(false);
    const [showEditAmountModal, setShowEditAmountModal] = useState(false);
    const [amountIncrease, setAmountIncrease] = useState('');
    const [isUpdatingAmount, setIsUpdatingAmount] = useState(false);
    const [reasonModal, setReasonModal] = useState({ isOpen: false, actionType: '', orderId: null, reason: '', isLoading: false, title: '' });

    // ─── Shiprocket state ─────────────────────────────────────────
    const SR_STEPS = { CREATE: 'create', PICK: 'pick', DISPATCHING: 'dispatching', DONE: 'done' };
    const SR_BASE  = ASSET_URL;
    const srHeaders = () => ({
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
    });

    const [srStep, setSrStep]                               = useState(SR_STEPS.CREATE);
    const [srCreatedOrder, setSrCreatedOrder]               = useState(null);
    const [srCourierOptions, setSrCourierOptions]           = useState([]);
    const [srLoadingCouriers, setSrLoadingCouriers]         = useState(false);
    const [srSelectedCourierId, setSrSelectedCourierId]     = useState(null);
    const [srSelectedCourierName, setSrSelectedCourierName] = useState('');
    const [srDispatchResult, setSrDispatchResult]           = useState(null);

    // ─── Dispatch Modal ───────────────────────────────────────────
    const DispatchModal = ({
        isOpen, onClose,
        selectedCourier, setSelectedCourier,
        isCreatingShipment, onDispatchSubmit,
        themeColor, courierApis,
        srStep, srCourierOptions, srLoadingCouriers,
        srSelectedCourierId, setSrSelectedCourierId,
        srSelectedCourierName, setSrSelectedCourierName,
        srDispatchResult, onSrAssignCourier, srCreatedOrder,
    }) => {
        if (!isOpen) return null;

        const isSR = selectedCourier === 'shiprocket';

        if (isSR && srStep === SR_STEPS.PICK) {
            return (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-bold">🚚 Pick a Delivery Partner</h3>
                            <button onClick={onClose} className="text-gray-400 text-2xl">×</button>
                        </div>
                        <div className="bg-gray-50 rounded-lg px-4 py-3 flex items-center justify-between text-sm mb-4">
                            <span className="text-gray-600">Order created on Shiprocket</span>
                            <span className="text-green-600 font-medium text-xs">✓ Ready to assign courier</span>
                        </div>
                        {srLoadingCouriers ? (
                            <div className="flex flex-col items-center justify-center py-12 gap-3">
                                <Loader className="w-8 h-8 animate-spin" style={{ color: themeColor }}/>
                                <p className="text-sm text-gray-500">Fetching available couriers...</p>
                            </div>
                        ) : srCourierOptions.length === 0 ? (
                            <div className="text-center py-10 text-gray-500">No couriers available for this pincode.</div>
                        ) : (
                            <>
                                <p className="text-xs text-gray-500 mb-3">{srCourierOptions.length} couriers available — sorted by price</p>
                                <div className="grid grid-cols-2 gap-3 max-h-[380px] overflow-y-auto pr-1">
                                    {srCourierOptions.map(courier => {
                                        const isSelected = srSelectedCourierId === courier.courier_company_id;
                                        return (
                                            <div key={courier.courier_company_id}
                                                onClick={() => { setSrSelectedCourierId(courier.courier_company_id); setSrSelectedCourierName(courier.courier_name); }}
                                                className="border-2 rounded-xl p-4 cursor-pointer transition-all hover:shadow-md"
                                                style={{ borderColor: isSelected ? themeColor : '#E5E7EB', backgroundColor: isSelected ? `${themeColor}10` : '#fff' }}
                                            >
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="flex items-center gap-2">
                                                        {isSelected && (
                                                            <div className="w-4 h-4 rounded-full flex items-center justify-center" style={{ backgroundColor: themeColor }}>
                                                                <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                                                                </svg>
                                                            </div>
                                                        )}
                                                        <span className="font-bold text-gray-900 text-sm">{courier.courier_name}</span>
                                                    </div>
                                                    <span className="text-lg font-bold" style={{ color: themeColor }}>₹{Math.round(courier.rate || 0)}</span>
                                                </div>
                                                <div className="flex items-center justify-between text-xs text-gray-500 mt-2">
                                                    <div className="flex items-center gap-1">
                                                        <Clock className="w-3 h-3"/>
                                                        <span>ETA: {courier.estimated_delivery_days || courier.etd || '3-5'} days</span>
                                                    </div>
                                                    {courier.rating && <span>⭐ {courier.rating}/5</span>}
                                                    {courier.cod === 1 && <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded-full text-[10px] font-medium">COD ✓</span>}
                                                </div>
                                                {courier.call_before_delivery === 1 && (
                                                    <div className="mt-2 text-[10px] text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full inline-block">📞 Call before delivery</div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                        <div className="flex items-center justify-between pt-4 border-t mt-4">
                            <div>
                                {srSelectedCourierId && (
                                    <p className="text-sm text-gray-600">Selected: <span className="font-semibold" style={{ color: themeColor }}>{srSelectedCourierName}</span></p>
                                )}
                            </div>
                            <div className="flex gap-3">
                                <button onClick={onClose} className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium">Cancel</button>
                                <button onClick={onSrAssignCourier} disabled={!srSelectedCourierId || srLoadingCouriers}
                                    className="px-8 py-2.5 text-white rounded-lg flex items-center gap-2 font-bold shadow-lg hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                                    style={{ backgroundColor: themeColor }}>
                                    <Truck className="w-5 h-5"/> Assign &amp; Dispatch
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        if (isSR && (srStep === SR_STEPS.DISPATCHING || srStep === SR_STEPS.DONE)) {
            return (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-lg mx-4">
                        <h3 className="text-xl font-bold mb-6">{srStep === SR_STEPS.DONE ? '📊 Dispatch Result' : 'Dispatching...'}</h3>
                        {srStep === SR_STEPS.DISPATCHING && (
                            <div className="flex flex-col items-center justify-center py-10 gap-4">
                                <Loader className="w-10 h-10 animate-spin" style={{ color: themeColor }}/>
                                <p className="text-gray-600">Assigning AWB & dispatching...</p>
                            </div>
                        )}
                        {srStep === SR_STEPS.DONE && srDispatchResult && (
                            <div className={`p-5 rounded-xl border ${srDispatchResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                                <p className="text-2xl mb-2">{srDispatchResult.success ? '✅' : '❌'}</p>
                                {srDispatchResult.success ? (
                                    <>
                                        <p className="font-bold text-green-800">Dispatched via {srDispatchResult.courierName}</p>
                                        <p className="text-sm font-mono bg-white px-3 py-1 rounded border border-green-100 inline-block mt-2">AWB: {srDispatchResult.awb}</p>
                                    </>
                                ) : (
                                    <p className="text-red-700 font-medium">{srDispatchResult.message}</p>
                                )}
                            </div>
                        )}
                        {srStep === SR_STEPS.DONE && (
                            <div className="flex justify-end mt-6">
                                <button onClick={() => { onClose(); if (srDispatchResult?.success) navigate(getBackPath()); }}
                                    className="px-8 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-black font-medium">Close</button>
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        if (isSR && srStep === SR_STEPS.CREATE && isCreatingShipment) {
            return (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-lg mx-4">
                        <div className="flex flex-col items-center justify-center py-10 gap-4">
                            <Loader className="w-10 h-10 animate-spin" style={{ color: themeColor }}/>
                            <p className="text-gray-600 font-medium">Creating order on Shiprocket...</p>
                        </div>
                    </div>
                </div>
            );
        }

        // Default: Courier selection screen
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-2xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xl font-bold">Dispatch Order</h3>
                        <button onClick={onClose} className="text-gray-400 text-2xl">×</button>
                    </div>
                    <div className="mb-6">
                        <label className="block text-sm font-medium mb-3">Select Courier</label>
                        <div className="grid grid-cols-2 gap-3">
                            {Object.entries(courierApis).map(([key, courier]) => (
                                <button key={key} onClick={() => setSelectedCourier(key)}
                                    className="p-4 rounded-lg border-2 flex flex-col items-center space-y-2"
                                    style={{ borderColor: selectedCourier === key ? themeColor : '#E5E7EB', backgroundColor: selectedCourier === key ? `${themeColor}15` : 'transparent' }}>
                                    <span className="text-2xl">{courier.logo}</span>
                                    <span className="text-[12px] font-medium">{courier.name}</span>
                                    {key === 'delhivery'  && <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Auto AWB</span>}
                                    {key === 'shiprocket' && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Pick Partner</span>}
                                    {key === 'fship'      && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">Auto AWB</span>}
                                    {key === 'shadowfax'  && <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">Auto AWB</span>}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="flex items-center justify-end space-x-3 mt-6">
                        <button onClick={onClose} className="px-6 py-2 border rounded-lg">Cancel</button>
                        <button onClick={onDispatchSubmit} disabled={isCreatingShipment || !selectedCourier}
                            className={`px-6 py-2 rounded-lg text-white flex items-center space-x-2 ${isCreatingShipment || !selectedCourier ? 'bg-gray-400 cursor-not-allowed' : ''}`}
                            style={{ backgroundColor: !isCreatingShipment && selectedCourier ? themeColor : undefined }}>
                            {isCreatingShipment
                                ? <><Loader className="w-4 h-4 animate-spin" /><span>Processing...</span></>
                                : <><Truck className="w-4 h-4" /><span>{selectedCourier === 'shiprocket' ? 'Next: Pick Courier →' : 'Dispatch'}</span></>
                            }
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const getBackPath = () => location.state?.from || localStorage.getItem('lastPath') || '/dashboard';

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchOrderDetails(); }, [id]);

    const fetchOrderDetails = async () => {
        try {
            setLoadingOrderDetails(true);
            const orderDetails = await ordersApi.getOrderDetails(id);
            setSelectedOrderDetails(orderDetails);
        } catch (error) {
            console.error('Error fetching order details:', error);
            Modal.error({ title: 'Error', content: 'Failed to load order details' });
            navigate(getBackPath());
        } finally {
            setLoadingOrderDetails(false);
        }
    };

    const handleUpdateAmount = async () => {
        if (!amountIncrease || isNaN(amountIncrease) || parseFloat(amountIncrease) <= 0) {
            Modal.warning({ title: 'Error', content: 'Please enter a valid amount' }); return;
        }
        try {
            setIsUpdatingAmount(true);
            const order = selectedOrderDetails?.order || {};
            await ordersApi.increaseOrderAmount(order.id, { increaseBy: parseFloat(amountIncrease) });
            await fetchOrderDetails();
            setShowEditAmountModal(false);
            setAmountIncrease('');
            setShowSuccessPopup(true);
            setTimeout(() => setShowSuccessPopup(false), 2000);
        } catch { Modal.error({ title: 'Error', content: 'Failed to update amount. Please try again.' }); }
        finally { setIsUpdatingAmount(false); }
    };

    const handleCustomerStatusUpdate = async (orderId, status) => {
        if (!status) return;
        try {
            const order = selectedOrderDetails?.order || {};
            const payload = { status };
            if (status === 'Delivered') {
                payload.notes = 'Customer ne receive kiya';
                if (order.status === 'OUT_FOR_DELIVERY') {
                    await ordersApi.markOFDAsDelivered(orderId, { deliveryNotes: 'Successfully delivered', deliveredTo: 'Customer' });
                }
            } else if (status === 'Convinced to receive') { payload.notes = 'Kal dobara aana'; }
            else if (status === 'Not connected')          { payload.notes = 'Phone nahi utha raha'; }
            else if (status === 'Not interested')         { payload.notes = 'Nahi lena'; }
            else if (status === 'Update contact number')  { payload.notes = 'Contact update required'; payload.newContactNumber = ''; }
            else                                          { payload.notes = `Status updated to: ${status}`; }

            if (order.status === 'OUT_FOR_DELIVERY') {
                await ordersApi.updateOFDCustomerStatus(orderId, payload);
            } else {
                await ordersApi.updateNDRCustomerStatus(orderId, payload);
            }
            message.success('Customer status updated successfully');
            fetchOrderDetails();
        } catch (error) {
            console.error('Error updating status:', error);
            message.error('Failed to update status');
        }
    };

    const handleReplaceOrder = async () => {
        try {
            const order = selectedOrderDetails?.order || {};
            await ordersApi.increaseOrderAmount(order.id, { increaseBy: order.grandTotal || 0 });
            setIsEditModalOpen(false);
            setShowSuccessPopup(true);
            setTimeout(() => { setShowSuccessPopup(false); navigate(getBackPath()); }, 2000);
        } catch (error) { Modal.error({ title: 'Error', content: 'Failed to update order: ' + (error.message || 'Unknown error') }); }
    };

    // ─── SR Step 1: Create order ──────────────────────────────────
   const handleShiprocketCreateOrder = async () => {
    const order = selectedOrderDetails?.order || {};
    
    // ✅ DEBUG LOG — console mein dekho
    console.log('🔍 ORDER DATA:', {
        paymentMode:   order.paymentMode,
        paymentType:   order.paymentType,
        grandTotal:    order.grandTotal,
        pendingAmount: order.pendingAmount,
        prepaidAmount: order.prepaidAmount,
    });

    setSrStep(SR_STEPS.CREATE);
    setIsCreatingShipment(true);
    try {
        const orderDate = new Date().toISOString().slice(0, 16).replace('T', ' ');

        // ✅ Payment logic from user
        const isCOD      = ['COD', 'CASH'].includes(order.paymentMode);
        const isPrepaid  = ['ONLINE', 'PREPAID', 'UPI', 'CARD'].includes(order.paymentMode);
        const isPartPay  = order.paymentType === 'PART_PAYMENT';
        const pendingAmt = Math.max(0,
            parseFloat(order.pendingAmount) ||
            parseFloat(order.restAmount)    ||
            ((parseFloat(order.grandTotal) || 0) - (parseFloat(order.discount) || 0) - (parseFloat(order.prepaidAmount) || 0))
        );

        // ✅ ONLINE/UPI/CARD + PART_PAYMENT = COD (pending collect karna hai)
        // ✅ ONLINE/UPI/CARD + FULL          = Prepaid
        // ✅ COD/CASH                         = COD
        const paymentMethod = (isPrepaid && !isPartPay) ? 'Prepaid' : 'COD';

        // ✅ sub_total — part payment me sirf pending amount
        const subTotal = isPartPay ? pendingAmt : (parseFloat(order.grandTotal) || 500);

        // ✅ cod_amount — Shiprocket isi se COD/Prepaid decide karta hai
        const codAmount = paymentMethod === 'COD'
            ? (isCOD ? (parseFloat(order.grandTotal) || 0) : pendingAmt)
            : 0;

        console.log('💳 PAYMENT DECISION:', {
            isCOD, isPrepaid, isPartPay,
            pendingAmt, subTotal, codAmount, paymentMethod,
        });

        const payload = {
            order_id:              String(order.orderId || order.id),
            order_date:            orderDate,
            pickup_location:       'Home',
            billing_customer_name: order.customerName?.split(' ')[0] || 'Customer',
            billing_last_name:     order.customerName?.split(' ').slice(1).join(' ') || '',
            billing_address:       order.address || 'Address',
            billing_city:          order.district || 'Delhi',
            billing_pincode:       String(order.areaPinCode || '110001'),
            billing_state:         order.state || 'Delhi',
            billing_country:       'India',
            billing_email:         order.email || 'noreply@indharahealthcare.com',
            billing_phone:         String(order.contactNumber || '9999999999'),
            shipping_is_billing:   true,
            payment_method:        paymentMethod,  // ✅ COD / Prepaid
            sub_total:             subTotal,        // ✅ pending amount for part payment
            cod_amount:            codAmount,       // ✅ Shiprocket isi se decide karta hai
            length: 10, breadth: 10, height: 10, weight: 0.5,
            order_items: (order.orderItems?.length > 0 ? order.orderItems : [order]).map(item => ({
                name:          item.productName || 'Product',
                sku:           String(item.sku || item.productId || 'SKU-001'),
                units:         item.quantity || 1,
                selling_price: item.unitPrice || item.grandTotal || order.grandTotal || 500,
                hsn:           item.hsnCode || '62091',
            })),
        };

        console.log('📦 PAYLOAD TO SHIPROCKET:', JSON.stringify(payload, null, 2));

        const res    = await fetch(`${SR_BASE}/api/shiprocket/order`, { method: 'POST', headers: srHeaders(), body: JSON.stringify(payload) });
        const result = await res.json();
        if (!res.ok) throw new Error(result.error || result.message || 'Order create failed');
        const shipmentId = result.shipment_id || result.order_id;
        if (!shipmentId) throw new Error('No shipment_id returned from Shiprocket');

        setSrCreatedOrder({
            orderId:    order.orderId || order.id,
            internalId: order.id,
            shipmentId: String(shipmentId),
            pincode:    String(order.areaPinCode || '110001'),
            cod:        paymentMethod === 'COD' ? 1 : 0,
        });

        await fetchSrCouriers(String(order.areaPinCode || '110001'), paymentMethod === 'COD' ? 1 : 0);
    } catch (err) {
        Modal.error({ title: 'Shiprocket Error', content: err.message });
        setShowDispatchModal(false);
    } finally {
        setIsCreatingShipment(false);
    }
};

    // ─── SR Step 2: Serviceability ────────────────────────────────
    const fetchSrCouriers = async (pincode, cod) => {
        setSrStep(SR_STEPS.PICK);
        setSrLoadingCouriers(true);
        setSrCourierOptions([]);
        try {
            const params = new URLSearchParams({ pickup: '110045', delivery: pincode, weight: '0.5', cod: String(cod) });
            const res    = await fetch(`${SR_BASE}/api/shiprocket/serviceability?${params}`, { headers: srHeaders() });
            const result = await res.json();
            if (!res.ok) throw new Error(result.error || result.message || 'Serviceability failed');
            const couriers = result?.data?.available_courier_companies || result?.available_courier_companies || [];
            if (couriers.length === 0) throw new Error('No couriers available for this pincode');
            const sorted = [...couriers].sort((a, b) => {
                if (a.recommended && !b.recommended) return -1;
                if (!a.recommended && b.recommended) return 1;
                return (a.rate || 0) - (b.rate || 0);
            });
            setSrCourierOptions(sorted);
        } catch (err) {
            Modal.error({ title: 'Courier Fetch Error', content: err.message });
            setSrCourierOptions([]);
        } finally {
            setSrLoadingCouriers(false);
        }
    };

    // ─── SR Step 3: AWB assign ────────────────────────────────────
    const handleSrAssignCourier = async () => {
        if (!srSelectedCourierId || !srCreatedOrder) { message.warning('Please select a courier'); return; }
        const order = selectedOrderDetails?.order || {};
        if (['SHIPPED', 'IN_TRANSIT', 'DELIVERED'].includes(order.status)) {
            message.warning('This order is already shipped.');
            setShowDispatchModal(false);
            return;
        }
        setSrStep(SR_STEPS.DISPATCHING);
        setIsCreatingShipment(true);
        try {
            const awbRes    = await fetch(`${SR_BASE}/api/shiprocket/awb?shipmentId=${srCreatedOrder.shipmentId}&courierId=${srSelectedCourierId}`, { method: 'POST', headers: srHeaders() });
            const awbResult = await awbRes.json();
            if (!awbRes.ok) throw new Error(awbResult.error || awbResult.message || 'AWB generation failed');
            const awb = awbResult?.awbCode || awbResult?.awb_code || awbResult?.response?.data?.awb_code || awbResult?.awb || '';
            if (!awb) throw new Error('No AWB returned — try a different courier');
            await ordersApi.dispatchOrder(order.id, {
                courierName:          srSelectedCourierName,
                trackingNumber:       awb,
                awbNumber:            awb,
                expectedDeliveryDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                remarks:              `Shiprocket — ${srSelectedCourierName}`,
                courierKey:           'shiprocket',
            });
            setSrDispatchResult({ success: true, awb, courierName: srSelectedCourierName });
            setSrStep(SR_STEPS.DONE);
            fetchOrderDetails();
        } catch (err) {
            setSrDispatchResult({ success: false, message: err.message });
            setSrStep(SR_STEPS.DONE);
        } finally {
            setIsCreatingShipment(false);
        }
    };

    // ─── Main dispatch handler ────────────────────────────────────
    const handleDispatchSubmit = async () => {
        if (!selectedCourier) { Modal.warning({ title: 'Error', content: 'Please select a courier service' }); return; }

        // ── Shiprocket ────────────────────────────────────────────
        if (selectedCourier === 'shiprocket') {
            setSrStep(SR_STEPS.CREATE);
            setSrCreatedOrder(null);
            setSrCourierOptions([]);
            setSrSelectedCourierId(null);
            setSrSelectedCourierName('');
            setSrDispatchResult(null);
            await handleShiprocketCreateOrder();
            return;
        }
// ── ParcelX ───────────────────────────────────────────────
if (selectedCourier === 'parcelx') {
    setIsCreatingShipment(true);
    const order = selectedOrderDetails?.order || {};
    try {
        const payload = {
            consignee_name:     order.customerName  || 'Customer',
            consignee_address1: order.address
                                || [order.houseNumber, order.streetName, order.colonyName, order.district]
                                   .filter(Boolean).join(', ')
                                || 'Address',
            consignee_pincode:  String(order.areaPinCode   || '110001'),
            consignee_mobile:   String(order.contactNumber || '9999999999'),
            client_order_id:    String(order.orderId || order.id),
            invoice_number:     String(order.orderId || order.id),
            payment_mode:       order.paymentMode === 'COD' ? 'Cod' : 'Prepaid',
            cod_amount:         String(order.paymentMode === 'COD'
                                  ? Math.max(0, (order.grandTotal - (order.prepaidAmount || 0)))
                                  : 0),
            order_amount:       String(order.grandTotal || 0),
            courier_type:       1,
            courier_code:       'PXXBS02',
            pick_address_id:    '98795',
            products: (order.orderItems?.length > 0 ? order.orderItems : [order]).map(item => ({
                product_sku:      String(item.sku || item.productId || 'SKU-001'),
                product_name:     item.productName || 'Product',
                product_value:    String(item.unitPrice || item.grandTotal || order.grandTotal || 0),
                product_quantity: String(item.quantity || 1),
            })),
        };

        const response = await fetch(`${API_URL}/parcelx/order/create`, {
            method:  'POST',
            headers: {
                'Content-Type':  'application/json',
                'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`,
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || errorData.error || `ParcelX API Error: ${response.status}`);
        }

        const result   = await response.json();
        const finalAwb = result.awbNumber || result.awb_number || result.tracking_number
                         || result.waybill || result.awb || result.shipment_id || '';
        if (!finalAwb) throw new Error('No AWB number returned from ParcelX');

        await ordersApi.dispatchOrder(order.id, {
            courierName:          'ParcelX',
            trackingNumber:       finalAwb,
            awbNumber:            finalAwb,
            expectedDeliveryDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            remarks:              'Dispatched via ParcelX',
            courierKey:           'parcelx',
        });

        Modal.success({ title: 'Success', content: `✅ Order Dispatched!\n\nCourier: ParcelX\nAWB: ${finalAwb}` });
        setShowDispatchModal(false); setSelectedCourier('');
        setTimeout(() => { fetchOrderDetails(); navigate(getBackPath()); }, 1500);
    } catch (error) {
        Modal.error({ title: 'Error', content: error.message });
    } finally {
        setIsCreatingShipment(false);
    }
    return;
}
        // ── FShip ─────────────────────────────────────────────────
        // ── Ekart ─────────────────────────────────────────────────
if (selectedCourier === 'ekart') {
    setIsCreatingShipment(true);
    const order = selectedOrderDetails?.order || {};
    try {
        const today = new Date().toISOString().split('T')[0];
        const payload = {
            order_number:              order.orderId || String(order.id),
            invoice_number:            order.orderId || String(order.id),
            invoice_date:              today,
            consignee_name:            order.customerName  || 'Customer',
            consignee_alternate_phone: order.alternateNumber || '9999999999',
            products_desc:             order.orderItems?.[0]?.productName || order.productName || 'Health Supplement',
            payment_mode:              order.paymentMode === 'COD' ? 'COD' : 'Prepaid',
            category_of_goods:         order.category || 'Health',
            total_amount:              parseFloat(order.grandTotal) || 0,
            tax_value:                 0.0,
            taxable_amount:            parseFloat(order.grandTotal) || 0,
            commodity_value:           String(parseFloat(order.grandTotal) || 0),
            cod_amount:                order.paymentMode === 'COD'
                                         ? parseFloat((order.grandTotal - (order.prepaidAmount || 0)).toFixed(2))
                                         : 0,
            quantity:                  order.orderItems?.[0]?.quantity || 1,
            weight:                    500,
            length:                    10,
            height:                    10,
            width:                     10,
            seller_name:               'Aarogya India Pvt Ltd',
            seller_address:            'Building No 23, Second Basement E Block, Sector 63, Noida',
            seller_gst_tin:            '09AAAAA0000A1Z5',
            consignee_gst_amount:      0.0,
            drop_location: {
                location_type: 'Home',
                address:       order.address
                               || [order.houseNumber, order.streetName, order.colonyName, order.district]
                                  .filter(Boolean).join(', ')
                               || 'Address',
                city:          order.cityTown || order.district || 'City',
                state:         order.state    || 'State',
                country:       'India',
                name:          order.customerName  || 'Customer',
                phone:         parseInt(order.contactNumber) || 9999999999,
                pin:           parseInt(order.areaPinCode)   || 110001,
            },
        };

        const response = await fetch(`${API_URL}/courier/ekart/shipment/create`, {
            method:  'POST',
            headers: {
                'Content-Type':  'application/json',
                'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`,
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            let errMsg = errorData.message || errorData.error || `Ekart API Error: ${response.status}`;
            try {
                const match = errMsg.match(/Ekart API error: (.+)/);
                if (match) {
                    const parsed = JSON.parse(match[1]);
                    errMsg = parsed.description || parsed.message || errMsg;
                }
            } catch (_) {}
            throw new Error(errMsg);
        }

        const result   = await response.json();
        const finalAwb = result.awbNumber || result.tracking_number
                         || result.waybill || result.awb
                         || result.shipment_id || '';
        if (!finalAwb) throw new Error('No AWB number returned from Ekart');

        await ordersApi.dispatchOrder(order.id, {
            courierName:          'Ekart',
            trackingNumber:       finalAwb,
            awbNumber:            finalAwb,
            expectedDeliveryDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            remarks:              'Dispatched via Ekart',
            courierKey:           'ekart',
        });

        Modal.success({ title: 'Success', content: `✅ Order Dispatched!\n\nCourier: Ekart\nAWB: ${finalAwb}` });
        setShowDispatchModal(false); setSelectedCourier('');
        setTimeout(() => { fetchOrderDetails(); navigate(getBackPath()); }, 1500);
    } catch (error) {
        let errMsg = error.message;
        try {
            const match = errMsg.match(/Ekart API error: (.+)/);
            if (match) {
                const parsed = JSON.parse(match[1]);
                errMsg = parsed.description || parsed.message || errMsg;
            }
        } catch (_) {}
        Modal.error({ title: 'Error', content: errMsg });
    } finally {
        setIsCreatingShipment(false);
    }
    return;
}
        if (selectedCourier === 'fship') {
            setIsCreatingShipment(true);
            const order = selectedOrderDetails?.order || {};
            try {
               const payload = {
      customer_Name:         order.customerName || 'Customer',
      customer_Mobile:       order.contactNumber || '9999999999',
      customer_Emailid:  order.email || 'noreply@aarogyaindia.com',
      customer_Address:      order.address || 'Address',
      landMark:          order.landmark || 'NA',
      customer_Address_Type: 'Home',
      customer_PinCode:      order.areaPinCode || '110001',
      customer_City:         order.district || 'City',
      orderId:               order.orderId || order.id,
      invoice_Number:        order.orderId || order.id,
      payment_Mode:          order.paymentMode === 'COD' ? 1 : 0,
      express_Type:          'surface',
      is_Ndd:                0,
      order_Amount:          order.grandTotal || 0,
      tax_Amount:            0,
      extra_Charges:         0,
      total_Amount:          order.grandTotal || 0,
      cod_Amount:            order.paymentMode === 'COD'
                               ? (order.grandTotal - (order.prepaidAmount || 0))
                               : 0,
      shipment_Weight:       0.5,
      shipment_Length:       20.0,
      shipment_Width:        15.0,
      shipment_Height:       10.0,
      volumetric_Weight:     0.6,
      pick_Address_ID:       220957,
      return_Address_ID:     220957,
      courierId:             59,
      products: (order.orderItems?.length > 0 ? order.orderItems : [order]).map(item => ({
        productName:     item.productName || 'Product',
        unitPrice:       item.unitPrice || item.grandTotal || 0,
        quantity:        item.quantity || 1,
        productCategory: item.category || order.category || 'General',
        hsnCode:         item.hsnCode || '30049099',
         sku:             String(item.sku || item.productId || 'SKU-001'),
        taxRate:         item.taxRate || 18.0,
      })),
    };
                const response = await fetch(`${API_URL}/fship/order/forward`, {
                    method:  'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
                    body: JSON.stringify(payload),
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.error || `FShip API Error: ${response.status}`);
                }
                const result   = await response.json();
                const finalAwb = result.awbNumber || result.trackingNumber || result.waybill || '';
                if (!finalAwb) throw new Error('No AWB number returned from FShip');
                await ordersApi.dispatchOrder(order.id, {
                    courierName:          'FShip',
                    trackingNumber:       finalAwb,
                    awbNumber:            finalAwb,
                    expectedDeliveryDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                    remarks:              'Dispatched via FShip',
                    courierKey:           'fship',
                });
                Modal.success({ title: 'Success', content: `✅ Order Dispatched!\n\nCourier: FShip\nAWB: ${finalAwb}` });
                setShowDispatchModal(false); setSelectedCourier('');
                setTimeout(() => { fetchOrderDetails(); navigate(getBackPath()); }, 1500);
            } catch (error) {
                Modal.error({ title: 'Error', content: error.message });
            } finally {
                setIsCreatingShipment(false);
            }
            return;
        }

        // ── Shadowfax ─────────────────────────────────────────────
        if (selectedCourier === 'shadowfax') {
            setIsCreatingShipment(true);
            const order = selectedOrderDetails?.order || {};
            try {
               const payload = {
      order_id:          `SFX-${order.orderId || order.id}`,
      order_type:        'forward',
      payment_type:      order.paymentMode === 'COD' ? 'cod' : 'prepaid',
      cod_amount:        order.paymentMode === 'COD'
                           ? (order.grandTotal - (order.prepaidAmount || 0))
                           : 0,
      invoice_value:     order.grandTotal || 0,
      invoice_number:    order.orderId || order.id,
      product_name:      order.orderItems?.[0]?.productName || order.productName || 'Product',
      product_category:  order.category || 'General',
      hsn_code:          order.orderItems?.[0]?.hsnCode || '30049099',
      package_weight:    500,
      package_length:    20.0,
      package_breadth:   15.0,
      package_height:    10.0,
      customer_name:     order.customerName     || 'Customer',
      customer_phone:    order.contactNumber    || '9999999999',
      customer_address:  order.address          || 'Address',
      customer_city:     order.district         || 'City',
      customer_state:    order.state            || 'State',
      customer_pincode:  order.areaPinCode      || '110001',
    };
                const response = await fetch(`${API_URL}/shadowfax/order/create`, {
                    method:  'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
                    body: JSON.stringify(payload),
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.error || `Shadowfax API Error: ${response.status}`);
                }
                const result   = await response.json();
                const finalAwb = result.awb_number || result.tracking_number || result.waybill || result.order_id || '';
                if (!finalAwb) throw new Error('No AWB number returned from Shadowfax');
                await ordersApi.dispatchOrder(order.id, {
                    courierName:          'Shadowfax',
                    trackingNumber:       finalAwb,
                    awbNumber:            finalAwb,
                    expectedDeliveryDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                    remarks:              'Dispatched via Shadowfax',
                    courierKey:           'shadowfax',
                });
                Modal.success({ title: 'Success', content: `✅ Order Dispatched!\n\nCourier: Shadowfax\nAWB: ${finalAwb}` });
                setShowDispatchModal(false); setSelectedCourier('');
                setTimeout(() => { fetchOrderDetails(); navigate(getBackPath()); }, 1500);
            } catch (error) {
                Modal.error({ title: 'Error', content: error.message });
            } finally {
                setIsCreatingShipment(false);
            }
            return;
        }

        // ── Delhivery ─────────────────────────────────────────────
        setIsCreatingShipment(true);
        const order = selectedOrderDetails?.order || {};
        try {
            let finalAwbNumber = '';
            const courierName  = COURIER_APIS[selectedCourier].name;
            const payload = {
            };
            const response = await fetch(`${API_URL}/shipment/create`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
                body: JSON.stringify(payload),
            });
            if (!response.ok) throw new Error(`API Error: ${response.status}`);
            const result = await response.json();
            if (!result.success) throw new Error(result.message || 'Shipment creation failed');
            finalAwbNumber = result.packages?.[0]?.waybill;
            if (!finalAwbNumber) throw new Error('No waybill generated');
            await ordersApi.dispatchOrder(order.id || order.orderId, {
                courierName, trackingNumber: finalAwbNumber, awbNumber: finalAwbNumber,
                expectedDeliveryDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                remarks: `Dispatched via ${courierName}`, courierKey: selectedCourier,
            });
            Modal.success({ title: 'Success', content: `✅ Order Dispatched!\n\nCourier: ${courierName}\nAWB: ${finalAwbNumber}` });
            setShowDispatchModal(false); setSelectedCourier('');
            setTimeout(() => { fetchOrderDetails(); navigate(getBackPath()); }, 1500);
        } catch (error) {
            Modal.error({ title: 'Error', content: error.message });
        } finally {
            setIsCreatingShipment(false);
        }
    };

    const handleVerifyOrder = async (orderId) => {
        try {
            await ordersApi.verifyOrder(orderId);
            Modal.success({ title: 'Success', content: 'Order verified!' });
            setTimeout(() => navigate(getBackPath(), {
                state: { activeTab: 'VERIFIED', currentPage: location.state?.currentPage }
            }), 1000);
        } catch { Modal.error({ title: 'Error', content: 'Failed to verify order' }); }
    };

    const openReasonModal = (orderId, type, title) => setReasonModal({ isOpen: true, actionType: type, orderId, reason: '', isLoading: false, title });

    const submitReasonAction = async () => {
        const { actionType, orderId, reason } = reasonModal;
        if (!reason.trim()) { Modal.warning({ title: 'Error', content: 'Please enter a reason.' }); return; }
        setReasonModal(prev => ({ ...prev, isLoading: true }));
        try {
            let targetTab = 'PROCESSING';
            if (actionType === 'HOLD') {
                await ordersApi.holdOrder(orderId, { reason });
                Modal.success({ title: 'Success', content: 'Order put on hold' });
                targetTab = 'ON_HOLD';
            } else if (actionType === 'REJECT') {
                await ordersApi.OrderStatus(orderId, 'REJECTED');
                Modal.success({ title: 'Success', content: 'Order rejected' });
                targetTab = 'REJECTED';
            } else if (actionType === 'PENDING') {
                await ordersApi.markAsPending(orderId, { reason });
                Modal.success({ title: 'Success', content: 'Order marked as pending' });
                targetTab = 'PENDING';
            }
            setReasonModal({ isOpen: false, actionType: '', orderId: null, reason: '', isLoading: false, title: '' });
            setTimeout(() => navigate(getBackPath(), {
                state: { activeTab: targetTab, currentPage: location.state?.currentPage }
            }), 1000);
        } catch {
            Modal.error({ title: 'Error', content: `Failed to ${reasonModal.actionType.toLowerCase()} order` });
            setReasonModal(prev => ({ ...prev, isLoading: false }));
        }
    };

    const handleHoldOrder    = (orderId) => openReasonModal(orderId, 'HOLD',    'Hold Order');
    const handleRejectOrder  = (orderId) => openReasonModal(orderId, 'REJECT',  'Reject Order');
    const handlePendingOrder = (orderId) => openReasonModal(orderId, 'PENDING', 'Mark as Pending');

    const getPageConfig = () => {
        const order = selectedOrderDetails?.order || {};
        const orderStatus = order.status || 'AMOUNT_DECREASE';
        const configs = {
            'PROCESSING':        { title: 'Decrease Request Order', showActions: ['hold', 'pending', 'reject', 'verify'] },
            'APPROVAL_PENDING':  { title: 'Processing Order',       showActions: ['hold', 'pending', 'reject', 'verify'] },
            'CONFIRMED':         { title: 'Verified Order',         showActions: ['hold', 'pending', 'verify'] },
            'VERIFIED':          { title: 'Verified Order',         showActions: ['hold', 'pending', 'dispatch'] },
            'ON_HOLD':           { title: 'Hold Order',             showActions: ['pending', 'verify'] },
            'HOLD':              { title: 'Hold Order',             showActions: ['pending', 'verify'] },
            'REJECTED':          { title: 'Rejected Order',         showActions: ['pending', 'verify'] },
            'PENDING':           { title: 'Pending Order',          showActions: ['hold', 'reject', 'verify'] },
            'OUT_FOR_DELIVERY':  { title: 'NDR Request',            showActions: [] },
            'NDR':               { title: 'NDR Order',              showActions: [] },
            'NDR_REQUEST':       { title: 'NDR Request',            showActions: [] },
        };
        return configs[orderStatus] || { title: orderStatus || 'Order Details', showActions: [] };
    };

    const renderActionButtons = (orderId) => {
        const config = getPageConfig();
        const actions = config.showActions;
        const permissionMap = {
            hold: canHold, pending: canPending, reject: canReject, verify: canVerify, dispatch: canDispatch,
        };
        const allowedActions = actions.filter(action => permissionMap[action]);
        if (allowedActions.length === 0) return null;
        const buttonConfig = {
            hold:     { onClick: () => handleHoldOrder(orderId),    className: "px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600",                                                     icon: "⏸", label: "Hold" },
            pending:  { onClick: () => handlePendingOrder(orderId), className: "px-6 py-3 bg-orange-400 text-white rounded-lg hover:bg-orange-500",                                                 icon: "🕐", label: "Pending" },
            reject:   { onClick: () => handleRejectOrder(orderId),  className: "px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600",                                                       icon: "✕",  label: "Reject" },
            verify:   { onClick: () => handleVerifyOrder(orderId),  className: "px-6 py-3 text-white rounded-lg font-semibold", style: { backgroundColor: currentTheme.color },                    icon: "✓",  label: "Verified Order" },
            dispatch: { onClick: () => setShowDispatchModal(true),  className: "px-8 py-3 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 font-semibold",                                       icon: "📦", label: "Dispatch" },
        };
        return (
            <div className="flex items-center space-x-3 pt-4">
                {allowedActions.map(action => {
                    const cfg = buttonConfig[action];
                    if (!cfg) return null;
                    return (
                        <button key={action} onClick={cfg.onClick} className={cfg.className}
                            style={{ ...cfg.style, marginLeft: action === 'verify' ? '450px' : undefined }}>
                            <span>{cfg.icon}</span> <span>{cfg.label}</span>
                        </button>
                    );
                })}
            </div>
        );
    };

    const displayValue  = (value) => value === null || value === undefined || value === '' || value === 0 ? '-/-' : value;
    const buildFullAddress = (src) => {
        const parts = [src.houseNumber, src.streetName, src.colonyName, src.landmark, src.cityTown || src.postOffice, src.district, src.state, src.areaPinCode].filter(Boolean);
        return parts.length > 0 ? parts.join(', ') : src.address || null;
    };

    if (loadingOrderDetails) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <Loader className="w-8 h-8 animate-spin mx-auto mb-4" style={{ color: currentTheme.color }} />
                    <p>Loading order details...</p>
                </div>
            </div>
        );
    }

    const order      = selectedOrderDetails?.order || {};
    const lead       = selectedOrderDetails?.lead  || {};
    const orderItems = order.orderItems || [];
    const pageConfig = getPageConfig();
    const addrSrc    = { ...lead, ...order };
    const fullAddress = buildFullAddress(addrSrc);

    return (
        <div className={`min-h-screen p-6 font-sans transition-colors duration-500 ${isDark ? 'bg-[#0F172A]' : 'bg-[#F8FAFC]'}`}>

            {/* Action Modal */}
            {showActionModal && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] backdrop-blur-sm" onClick={() => setShowActionModal(false)}>
                    <div className={`rounded-3xl p-8 w-full max-w-md shadow-2xl relative border ${isDark ? 'bg-[#111827] border-white/10' : 'bg-white border-gray-100'}`} onClick={e => e.stopPropagation()}>
                        <button onClick={() => setShowActionModal(false)} className={`absolute top-6 right-6 p-2 rounded-full transition-all ${isDark ? 'hover:bg-white/10 text-white' : 'hover:bg-black/5 text-gray-400'}`}><X className="w-6 h-6" /></button>
                        <div className="space-y-4 pt-8">
                            {canVerify && (
                                <button onClick={() => { handleVerifyOrder(order.id); setShowActionModal(false); }}
                                    className={`w-full p-6 rounded-2xl border-2 transition-all text-center group active:scale-95 ${isDark ? 'bg-white/5 border-white/5 hover:border-blue-500/50' : 'bg-gray-50 border-gray-100 hover:border-teal-200'}`}>
                                    <h1 className={`text-xl font-black uppercase tracking-widest ${isDark ? 'text-gray-300 group-hover:text-white' : 'text-gray-700 group-hover:text-teal-600'}`}>Verified Order</h1>
                                </button>
                            )}
                            {canDispatch && (
                                <button onClick={() => { setShowDispatchModal(true); setShowActionModal(false); }}
                                    className={`w-full p-6 rounded-2xl border-2 transition-all text-center group active:scale-95 ${isDark ? 'bg-white/5 border-white/5 hover:border-blue-500/50' : 'bg-gray-50 border-gray-100 hover:border-teal-200'}`}>
                                    <h1 className={`text-xl font-black uppercase tracking-widest ${isDark ? 'text-gray-300 group-hover:text-white' : 'text-gray-700 group-hover:text-teal-600'}`}>Dispatch Order</h1>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Dispatch Modal */}
            {showDispatchModal && canDispatch && (
                <DispatchModal
                    isOpen={showDispatchModal}
                    onClose={() => setShowDispatchModal(false)}
                    selectedCourier={selectedCourier}
                    setSelectedCourier={setSelectedCourier}
                    dispatchFormData={dispatchFormData}
                    setDispatchFormData={setDispatchFormData}
                    isCreatingShipment={isCreatingShipment}
                    onDispatchSubmit={handleDispatchSubmit}
                    themeColor={currentTheme.color}
                    courierApis={COURIER_APIS}
                    srStep={srStep}
                    srCourierOptions={srCourierOptions}
                    srLoadingCouriers={srLoadingCouriers}
                    srSelectedCourierId={srSelectedCourierId}
                    setSrSelectedCourierId={setSrSelectedCourierId}
                    srSelectedCourierName={srSelectedCourierName}
                    setSrSelectedCourierName={setSrSelectedCourierName}
                    srDispatchResult={srDispatchResult}
                    onSrAssignCourier={handleSrAssignCourier}
                    srCreatedOrder={srCreatedOrder}
                />
            )}

            {showEditAmountModal && canEditAmount && (
                <EditAmountModal isOpen={showEditAmountModal} onClose={() => { setShowEditAmountModal(false); setAmountIncrease(''); }}
                    order={order} amountIncrease={amountIncrease} setAmountIncrease={setAmountIncrease}
                    isUpdatingAmount={isUpdatingAmount} onUpdateAmount={handleUpdateAmount} isDark={isDark} themeColor={currentTheme.color} />
            )}

            <ReasonModal reasonModal={reasonModal} setReasonModal={setReasonModal} onSubmit={submitReasonAction} isDark={isDark} themeColor={currentTheme.color} />

            {/* Header */}
            <div className="flex justify-between items-center mb-8">
                <div className="flex items-center gap-4">
                    <button onClick={() => {
                        if (location.state?.from) {
                            navigate(location.state.from, {
                                state: { activeTab: location.state?.activeTab, currentPage: location.state?.currentPage }
                            });
                        } else { navigate(-1); }
                    }} className={`p-3 rounded-2xl transition-all shadow-lg active:scale-90 ${isDark ? 'bg-white/5 text-white hover:bg-white/10' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
                        <ArrowLeft className="w-6 h-6" style={{ color: currentTheme.color }} />
                    </button>
                    <div>
                        <h1 className={`text-2xl font-black uppercase tracking-[0.2em] ${isDark ? 'text-white' : 'text-gray-800'}`}>{pageConfig.title}</h1>
                        <p className={`text-[10px] font-black uppercase tracking-widest mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Details & Management</p>
                    </div>
                </div>
                <div className={`px-6 py-2 rounded-xl text-xs font-black uppercase tracking-[0.2em] shadow-xl text-white`} style={{ backgroundColor: currentTheme.color }}>
                    {order.status || 'Decrease Request'}
                </div>
            </div>

            {showSuccessPopup && (
                <div className="fixed top-24 left-1/2 transform -translate-x-1/2 bg-emerald-500 text-white px-8 py-4 rounded-2xl shadow-2xl z-[100] flex items-center gap-3 animate-bounce">
                    <CheckCircle className="w-6 h-6" /><span className="font-black uppercase tracking-widest text-xs">Updated successfully</span>
                </div>
            )}

            {/* Top Banner */}
            <div className="rounded-3xl overflow-hidden flex items-stretch shadow-2xl mb-8 border transition-all" style={{ borderColor: isDark ? 'rgba(255,255,255,0.05)' : 'transparent' }}>
                <div className="p-8 flex-1 grid grid-cols-3 gap-8" style={{ background: isDark ? '#111827' : 'white' }}>
                    <div>
                        <p className={`text-[10px] font-black uppercase tracking-widest mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Order ID</p>
                        <p className={`text-2xl font-black ${isDark ? 'text-white' : 'text-gray-900'}`}>{displayValue(order.orderId)}</p>
                    </div>
                    <div className={`border-l pl-8 ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                        <p className={`text-[10px] font-black uppercase tracking-widest mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Order Date</p>
                        <p className={`text-lg font-black ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{formatDate(order.orderDate)}</p>
                    </div>
                    <div className={`border-l pl-8 ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                        <p className={`text-[10px] font-black uppercase tracking-widest mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Primary Product</p>
                        <p className={`text-lg font-black truncate ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{orderItems.length > 0 ? orderItems[0].productName : 'Product'}</p>
                    </div>
                </div>
                <div className="px-10 flex flex-col justify-center items-center text-white min-w-[220px] relative overflow-hidden" style={{ backgroundColor: currentTheme.color }}>
                    <div className="absolute inset-0 bg-black/10" />
                    <div className="relative z-10 flex flex-col items-center">
                        <span className="text-[10px] font-black uppercase tracking-[0.2em] opacity-80 mb-1">Rest Amount</span>
                        <span className="text-3xl font-black tabular-nums">₹ {order.restAmount || Math.max(0, (order.grandTotal - (order.prepaidAmount || 0)))}</span>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">

                    {/* Customer Details */}
                    <div className={`p-6 rounded-3xl shadow-xl border transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
                        <SectionHeader title="Customer Details" orderId={id} onEdit={navigate} orderStatus={order.status} canEdit={canEdit} isDark={isDark} themeColor={currentTheme.color} />
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                            <InfoCard icon={User}  label="Customer name"    value={displayValue(order.customerName || lead.customerName)}   themeColor={currentTheme.color} isDark={isDark} />
                            <InfoCard icon={Phone} label="Contact number"   value={displayValue(order.contactNumber || lead.contactNumber)} themeColor={currentTheme.color} isDark={isDark} />
                            <InfoCard icon={Phone} label="Alternate number" value={displayValue(order.alternateNumber || lead.alternateNumber)} themeColor={currentTheme.color} isDark={isDark} />
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <InfoCard icon={Mail}  label="Email"  value={displayValue(order.email || lead.email)}   themeColor={currentTheme.color} isDark={isDark} />
                            <InfoCard icon={User2} label="Gender" value={displayValue(order.gender || lead.gender)} themeColor={currentTheme.color} isDark={isDark} />
                            <InfoCard icon={User}  label="Age"    value={displayValue(order.age || lead.age)}       themeColor={currentTheme.color} isDark={isDark} />
                            <InfoCard icon={Ruler} label="Height" value={displayValue(order.height || lead.height)} themeColor={currentTheme.color} isDark={isDark} />
                        </div>
                    </div>

                    {/* Basic Details */}
                    <div className={`p-6 rounded-3xl shadow-xl border transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
                        <SectionHeader title="Basic Details" orderId={id} onEdit={navigate} orderStatus={order.status} canEdit={canEdit} isDark={isDark} themeColor={currentTheme.color} />
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <InfoCard icon={Scale}        label="Weight"                    value={displayValue(order.weight || lead.weight)}                                                   themeColor={currentTheme.color} isDark={isDark} />
                            <InfoCard icon={Activity}     label="Actual Problem"            value={displayValue(order.actualProblem || lead.actualProblem)}                                    themeColor={currentTheme.color} isDark={isDark} />
                            <InfoCard icon={Stethoscope}  label="Medical Treatment" value={displayValue(order.existingMedicalTreatment || lead.existingMedicalTreatment)}           themeColor={currentTheme.color} isDark={isDark} />
                            <InfoCard icon={Heart}        label="Marital Status"             value={displayValue(order.maritalStatus || lead.maritalStatus)}                                   themeColor={currentTheme.color} isDark={isDark} />
                        </div>
                    </div>

                    {/* Address */}
                    <div className={`p-6 rounded-3xl shadow-xl border transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
                        <SectionHeader title="Address" orderId={id} onEdit={navigate} orderStatus={order.status} canEdit={canEdit} isDark={isDark} themeColor={currentTheme.color} />
                        {addrSrc.addressType && (
                            <div className="mb-4"><span className={`inline-flex items-center px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border transition-all ${isDark ? 'bg-white/5 text-teal-400 border-teal-500/30' : 'bg-teal-50 text-teal-700 border-teal-200'}`}>📍 {addrSrc.addressType}</span></div>
                        )}
                        <div className={`p-5 rounded-2xl mb-6 flex items-start gap-4 border transition-all ${isDark ? 'bg-white/5 border-white/5' : 'bg-teal-50/50 border-teal-100'}`}>
                            <Home className="w-6 h-6 mt-1 flex-shrink-0" style={{ color: currentTheme.color }} />
                            <span className={`text-sm font-black tracking-wide ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{displayValue(fullAddress)}</span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                            <InfoCard icon={Home}    label="House / Flat" value={displayValue(addrSrc.houseNumber)} themeColor={currentTheme.color} isDark={isDark} />
                            <InfoCard icon={MapPin}  label="Street"           value={displayValue(addrSrc.streetName)}  themeColor={currentTheme.color} isDark={isDark} />
                            <InfoCard icon={Building} label="Colony"       value={displayValue(addrSrc.colonyName)}  themeColor={currentTheme.color} isDark={isDark} />
                            <InfoCard icon={MapPin}  label="Landmark"                value={displayValue(addrSrc.landmark)}    themeColor={currentTheme.color} isDark={isDark} />
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <InfoCard icon={MapPin}   label="Pin code" value={displayValue(addrSrc.areaPinCode)}              themeColor={currentTheme.color} isDark={isDark} />
                            <InfoCard icon={Building} label="Post Office"   value={displayValue(addrSrc.cityTown || addrSrc.city)} themeColor={currentTheme.color} isDark={isDark} />
                            <InfoCard icon={MapPin}   label="District"      value={displayValue(addrSrc.district)}                 themeColor={currentTheme.color} isDark={isDark} />
                            <InfoCard icon={Home}     label="State"         value={displayValue(addrSrc.state)}                    themeColor={currentTheme.color} isDark={isDark} />
                        </div>
                    </div>

                    {/* Remarks */}
                    <div className={`p-6 rounded-3xl shadow-xl border transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
                        <h3 className={`text-sm font-black uppercase tracking-[0.2em] mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>Remarks</h3>
                        <div className={`p-4 rounded-xl border italic ${isDark ? 'bg-white/5 border-white/5 text-gray-400' : 'bg-gray-50 border-gray-100 text-gray-600'}`}>
                            "{order.remarks === 'stdtwyteyytwe' ? '-' : (displayValue(order.remarks) || 'No remarks provided.')}"
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className={`rounded-3xl shadow-xl border transition-all overflow-hidden ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
                        <div className={`flex border-b ${isDark ? 'border-white/5 bg-white/2' : 'border-gray-100 bg-gray-50/30'}`}>
                            {['callLogs', 'orders'].map((tab) => (
                                <button key={tab} onClick={() => setActiveTab(tab)} 
                                    className={`px-8 py-5 text-[10px] font-black uppercase tracking-[0.2em] transition-all relative ${activeTab === tab ? (isDark ? 'text-white' : 'text-gray-900') : 'text-gray-400 hover:text-gray-500'}`}>
                                    {tab === 'callLogs' ? 'Call logs' : 'Order history'}
                                    {activeTab === tab && (
                                        <div className="absolute bottom-0 left-0 right-0 h-1 rounded-t-full shadow-[0_-4px_10px_rgba(0,0,0,0.1)]" style={{ backgroundColor: currentTheme.color }} />
                                    )}
                                </button>
                            ))}
                        </div>
                        <div className="p-12 text-center">
                            <div className={`w-16 h-16 rounded-3xl flex items-center justify-center mx-auto mb-4 ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                                <Clock className="w-8 h-8 text-gray-400 opacity-50" />
                            </div>
                            <p className={`text-xs font-black uppercase tracking-widest ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                {activeTab === 'callLogs' ? 'No call logs recorded' : 'No order history available'}
                            </p>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    {renderActionButtons(order.id)}
                </div>

                {/* Right Column */}
                <div className="space-y-6">

                    {/* NDR Request Section */}
                    {((order.status === 'OUT_FOR_DELIVERY' && canOFDUpdate) ||
                      ((order.status === 'NDR' || order.status === 'NDR_REQUEST') && canNDREdit)) && (
                        <div className={`p-6 rounded-3xl shadow-xl border transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
                            <h3 className={`text-sm font-black uppercase tracking-[0.2em] mb-6 flex items-center gap-3 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                                <Truck className="w-5 h-5" style={{ color: currentTheme.color }} /> NDR Request
                            </h3>
                            <div className="space-y-6">
                                <div>
                                    <label className={`block text-[10px] font-black uppercase tracking-widest mb-3 ${isDark ? 'text-gray-500' : 'text-gray-700'}`}>Status Update</label>
                                    <select
                                        className={`w-full px-5 py-3 border rounded-xl text-sm font-black transition-all outline-none appearance-none cursor-pointer ${isDark ? 'bg-white/5 border-white/10 text-white focus:border-blue-500/50' : 'bg-gray-50 border-gray-200 text-gray-800 focus:border-teal-500'}`}
                                        onChange={(e) => handleCustomerStatusUpdate(order.id, e.target.value)}
                                        value={order.customerStatus || ""}
                                    >
                                        <option value="">Select Status</option>
                                        <option value="Delivered">Delivered</option>
                                        <option value="Convinced to receive">Convinced to receive</option>
                                        <option value="Not connected">Not connected</option>
                                        <option value="Not interested">Not interested</option>
                                        <option value="Update contact number">Update contact number</option>
                                        <option value="Others">Others</option>
                                    </select>
                                </div>
                                {order.customerStatus && (
                                    <div className={`p-4 rounded-2xl border flex items-center gap-4 transition-all ${isDark ? 'bg-white/5 border-white/5' : 'bg-emerald-50 border-emerald-100'}`}>
                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white text-emerald-600 shadow-sm'}`}>
                                            <CheckCircle className="w-6 h-6" />
                                        </div>
                                        <div>
                                            <p className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-gray-500' : 'text-emerald-600/70'}`}>Current Status</p>
                                            <p className={`text-sm font-black mt-0.5 ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>{order.customerStatus}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Product Details */}
                    <div className={`p-6 rounded-3xl shadow-xl border transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
                        <SectionHeader title="Product Details" orderId={id} onEdit={navigate} orderStatus={order.status} canEdit={canEdit} isDark={isDark} themeColor={currentTheme.color} />
                        <div className="space-y-4">
                            {orderItems.length > 0 ? orderItems.map((item, idx) => (
                                <div key={idx} className={`flex gap-4 items-center p-4 rounded-2xl border transition-all ${isDark ? 'bg-white/2 border-white/5 hover:bg-white/5' : 'bg-gray-50/50 border-gray-100 hover:bg-white hover:shadow-md'}`}>
                                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center border ${isDark ? 'bg-white/5 border-white/5' : 'bg-white border-gray-200'}`}>
                                        <Package className={`w-7 h-7 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className={`font-black text-sm truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{item.productName}</p>
                                        <p className={`text-[10px] font-black uppercase tracking-widest mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{item.productSku || 'SKU N/A'}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-1">Qty: {item.quantity}</p>
                                        <p className="font-black text-lg" style={{ color: currentTheme.color }}>₹ {item.totalPrice || item.unitPrice}</p>
                                    </div>
                                </div>
                            )) : <div className={`text-center py-8 text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>No products found</div>}
                        </div>
                    </div>

                    {/* Timeline */}
                    <div className={`p-6 rounded-3xl shadow-xl border transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
                        <h3 className={`text-sm font-black uppercase tracking-[0.2em] mb-8 ${isDark ? 'text-white' : 'text-gray-800'}`}>Order Timeline</h3>
                        <div className="space-y-8 relative ml-4">
                            {/* Vertical Line */}
                            <div className={`absolute left-[15px] top-2 bottom-2 w-0.5 border-l-2 border-dashed transition-all ${isDark ? 'border-white/10' : 'border-gray-200'}`} />

                            {[
                                { date: order.orderDate,    label: 'Order by',   name: order.createdByName   },
                                { date: order.verifiedAt,   label: 'Verified by', name: order.verifiedByName || order.holdByName  },
                                { date: order.dispatchedAt, label: 'Dispatch by', name: order.dispatchedByName },
                                { date: order.ndrAt,        label: 'NDR by',      name: order.ndrByName       },
                            ].map((step, idx) => (
                                <div key={idx} className={`relative flex items-start gap-6 transition-all ${!step.date ? 'opacity-30 grayscale' : ''}`}>
                                    {/* Point */}
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center z-10 transition-all shadow-lg ${step.date ? 'scale-110' : 'scale-90 bg-gray-200'}`} 
                                         style={step.date ? { backgroundColor: currentTheme.color, boxShadow: `0 0 15px ${currentTheme.color}66` } : {}}>
                                        {step.date ? <CheckCircle className="w-5 h-5 text-white" /> : <div className="w-2 h-2 rounded-full bg-gray-400" />}
                                    </div>
                                    
                                    <div className="flex-1">
                                        <p className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{step.date ? formatDate(step.date) : 'Pending'}</p>
                                        <div className={`mt-2 p-4 rounded-2xl border transition-all ${step.date && isDark ? 'bg-white/5 border-white/10' : (step.date ? 'bg-gray-50 border-gray-100' : 'border-transparent')}`}>
                                            <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{step.label}</p>
                                            <p className={`text-sm font-black ${isDark ? 'text-white' : 'text-gray-900'}`}>{displayValue(step.name)}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Order Summary */}
                    <div className={`p-6 rounded-3xl shadow-xl border transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
                        <div className="grid grid-cols-2 gap-4">
                            {[
                                { label: 'Shipping', value: order.shippingMethod, color: isDark ? 'text-blue-400' : 'text-blue-600' },
                                { label: 'Payment Type', value: order.paymentType, color: isDark ? 'text-purple-400' : 'text-purple-600' },
                                { label: 'Payment Mode', value: order.paymentMode, color: isDark ? 'text-orange-400' : 'text-orange-600' },
                                { label: 'Prepaid', value: `₹ ${order.prepaidAmount || 0}`, color: 'text-emerald-500' },
                                { label: 'Discount', value: `₹ ${order.discount || 0}`, color: 'text-red-400' },
                                { label: 'Status', value: order.status, color: 'text-white', bg: currentTheme.color }
                            ].map((item, idx) => (
                                <div key={idx} className={`p-4 rounded-2xl border transition-all ${isDark ? 'bg-white/2 border-white/5' : 'bg-gray-50/50 border-gray-100'}`} style={item.bg ? { backgroundColor: item.bg, borderColor: item.bg } : {}}>
                                    <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${item.bg ? 'text-white/70' : (isDark ? 'text-gray-500' : 'text-gray-400')}`}>{item.label}</p>
                                    <p className={`text-xs font-black truncate ${item.bg ? 'text-white' : (item.color || (isDark ? 'text-gray-200' : 'text-gray-800'))}`}>{displayValue(item.value)}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Current Summary */}
                    <div className={`p-8 rounded-3xl shadow-2xl border transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
                        <div className='flex justify-between items-center mb-8'>
                            <h3 className={`text-sm font-black uppercase tracking-[0.2em] ${isDark ? 'text-white' : 'text-gray-800'}`}>Financial Summary</h3>
                            {canEditAmount && EDIT_ALLOWED_STATUSES.includes(order.status) && (
                                <button onClick={() => setShowEditAmountModal(true)} className={`p-3 rounded-2xl transition-all shadow-lg active:scale-90 ${isDark ? 'bg-white/5 text-orange-400 hover:bg-white/10' : 'bg-orange-50 text-orange-500 hover:bg-orange-100'}`}>
                                    <Edit className="w-5 h-5" />
                                </button>
                            )}
                        </div>
                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <span className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Sub Total</span>
                                <span className={`text-sm font-black ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>₹{order.subTotal || order.grandTotal}</span>
                            </div>
                            <div className="flex justify-between items-center text-emerald-500">
                                <span className="text-[10px] font-black uppercase tracking-widest opacity-80">Prepaid Amount</span>
                                <span className="text-sm font-black">₹{order.prepaidAmount || 0}</span>
                            </div>
                            <div className="flex justify-between items-center text-emerald-500">
                                <span className="text-[10px] font-black uppercase tracking-widest opacity-80">Total Discounts</span>
                                <span className="text-sm font-black">₹{order.discount || order.totalDiscounts || 0}</span>
                            </div>
                            <div className="flex justify-between items-center text-emerald-500">
                                <span className="text-[10px] font-black uppercase tracking-widest opacity-80">Shipping</span>
                                <span className="text-sm font-black">{order.shippingCharges > 0 ? `₹${order.shippingCharges}` : 'Free'}</span>
                            </div>
                            <div className="pt-6 border-t border-dashed mt-4" style={{ borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}>
                                <div className="flex justify-between items-center">
                                    <span className={`text-xs font-black uppercase tracking-[0.2em] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Net Payable:</span>
                                    <span className="text-3xl font-black" style={{ color: currentTheme.color }}>₹{Math.max(0, (parseFloat(order.grandTotal) || 0) - (parseFloat(order.prepaidAmount) || 0))}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Old Edit Modal */}
            {isEditModalOpen && canEdit && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden relative shadow-xl">
                        <div className="bg-[#bbf7d0] p-4 flex justify-between items-center">
                            <h2 className="text-lg font-semibold text-gray-800">Edit Summary</h2>
                            <button onClick={() => setIsEditModalOpen(false)} className="text-gray-600 hover:bg-black/10 p-1 rounded-full"><X className="w-6 h-6" /></button>
                        </div>
                        <div className="p-6 space-y-5">
                            <div>
                                <h3 className="text-sm font-medium text-gray-700 mb-2">Current Summary</h3>
                                <div className="border border-gray-200 rounded-lg overflow-hidden">
                                    <div className="p-4 bg-white space-y-2 text-sm">
                                        <div className="flex justify-between"><span className="text-gray-400">Sub total</span><span className="font-semibold text-gray-800">₹{order.subTotal || order.grandTotal}</span></div>
                                        <div className="flex justify-between text-teal-600"><span>Prepaid Amount</span><span>₹{order.prepaidAmount || 0}</span></div>
                                        <div className="flex justify-between text-teal-600"><span>Total Discounts</span><span>₹{order.discount || order.totalDiscounts || 0}</span></div>
                                        <div className="flex justify-between text-teal-600"><span>Shipping Charges</span><span>{order.shippingCharges > 0 ? `₹${order.shippingCharges}` : 'Free'}</span></div>
                                    </div>
                                    <div className="bg-[#7cc9b5] text-white p-3 flex justify-between items-center font-bold text-lg">
                                        <span>Amount:</span><span>₹{Math.max(0, (order.grandTotal || 0) - (order.prepaidAmount || 0))}</span>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Convinced amount</label>
                                <div className="relative">
                                    <input key={modalDefaultAmount} type="text" defaultValue={modalDefaultAmount}
                                        className="w-full border border-gray-300 rounded-md py-2 pl-6 pr-3 focus:outline-none focus:ring-1 focus:ring-teal-500" />
                                    <span className="absolute left-3 top-2 text-teal-600 font-medium">₹</span>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                                <input type="text" defaultValue={order.remarks || "Customer want to pay less"}
                                    className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-1 focus:ring-teal-500" />
                            </div>
                            <div className="flex justify-end pt-2">
                                <button onClick={handleReplaceOrder} className="bg-[#4a9d85] hover:bg-[#3d8b73] text-white font-medium px-6 py-2.5 rounded-lg transition-colors">Re-place order</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AmountDecreaseOrderDetails;